/**
 * ENG-5865 — atomic state-file writes.
 *
 * Background. `manager-state.json` is consumed by at least two external
 * readers — the synthetic-probe and `agt status` — and is written by the
 * manager on every poll completion + on dashboard restart-acks. Today the
 * write is a plain `writeFileSync(path, JSON.stringify(state))`, which on
 * POSIX filesystems means `open(O_TRUNC) → write → close`. Between truncate
 * and the synchronous write completing, any concurrent reader sees an
 * empty or torn file; `JSON.parse` throws; the synthetic-probe reports the
 * agent as down; an alarm fires for what was actually a quarter-millisecond
 * write window. We've not seen this fire frequently in production but it
 * IS the silent class of false-positive that's worst to debug after the
 * fact ("the agent was clearly alive — why did the probe alarm?").
 *
 * The fix is the textbook one: write to a tmp file on the same filesystem,
 * fsync the tmp file's data + the directory entry, then `rename(tmp, target)`.
 * rename(2) is atomic on POSIX (APFS, ext4, xfs all guarantee this) — a
 * reader either sees the previous version or the new one, never partial.
 *
 * Kept as a small pure module so the swap site stays a one-liner change at
 * each writeFileSync call, and so the helper is unit-testable without
 * mocking the manager.
 */

import { closeSync, fsyncSync, openSync, writeSync, renameSync, mkdirSync } from 'node:fs';
import { dirname } from 'node:path';

/**
 * Atomically write `data` to `path`. Creates parent directories if missing.
 *
 * Guarantees (on POSIX filesystems): a concurrent reader of `path` sees
 * either the previous content of `path` or `data` in full — never an empty
 * file, never a torn write. On Windows the rename(2) atomicity guarantee is
 * weaker but the practical behaviour is the same as POSIX for non-shared
 * files (which this is — the manager has a PID-lock).
 *
 * Best-effort fsync: a failing fsync (e.g., EIO during a transient disk
 * fault) is logged-and-continued rather than thrown, because the durability
 * guarantee is nice-to-have and the atomicity guarantee (via rename) is
 * already met by the time we reach the fsync call. Throwing here would
 * regress callers that today succeed with plain writeFileSync.
 */
export function atomicWriteFileSync(path: string, data: string): void {
  // Same directory as the target so rename is a within-filesystem move and
  // therefore atomic. A `.tmp.<pid>.<rand>` suffix avoids collision when
  // two processes race the same target (the loser's rename overwrites the
  // winner's content, but each individual reader still sees a consistent
  // snapshot — which is what atomicity buys us).
  const dirPath = dirname(path);
  const tmpPath = `${path}.tmp.${process.pid}.${Math.random().toString(36).slice(2, 8)}`;

  try { mkdirSync(dirPath, { recursive: true }); } catch { /* parent exists or unwritable */ }

  const fd = openSync(tmpPath, 'w', 0o644);
  try {
    writeSync(fd, data);
    // fsync persists the data so even a power loss preserves it. Skip on
    // failure — the rename below still gives atomicity for live readers.
    try { fsyncSync(fd); } catch { /* non-fatal */ }
  } finally {
    closeSync(fd);
  }
  renameSync(tmpPath, path);

  // CodeRabbit (PR #1631): rename(2) is atomic but the directory entry for
  // `path` is not necessarily durable across crash/power loss until the
  // PARENT directory is fsync'd — fsyncSync on the file fd alone covers
  // the data, not the rename. Open the dir RDONLY and fsync. Best-effort:
  // Windows + some macOS fs setups don't support directory fsync; we'd
  // rather degrade silently than refuse to write.
  try {
    const dirFd = openSync(dirPath, 'r');
    try { fsyncSync(dirFd); } finally { closeSync(dirFd); }
  } catch { /* non-fatal */ }
}
